import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
  type MutableRefObject,
} from "react";
import Text from "./Text";
import { CategorySelector } from "./CategorySelector";
import { ItemCardList } from "./ItemCardList";
import SidebarFloorSelector from "./SidebarFloorSelector";
import { MetadataItem } from "../types/metadata";
import SidebarFacilityInformation from "./SidebarFacilityInformation";
import SidebarNormalRoute from "./SidebarRouteNavigation";
import { RoutingMode } from "@/features/pathFinding/types/routing";
import { DIGITAL_SIGNAGE_LOCATION, BUILDINGS } from "@/config/constants";
import { LocationInput } from "@/features/pathFinding/types/routingInterface";
import SearchWithVirtualkeyboard from "./SearchWithVirtualkeyboard";
import { filterItems, getCountsByCategory, groupItemsByFloor } from "@/utils/filterItems";
import { fetchMetadata } from "@/utils/mapDataUtils";
import InformationButton from "./InformationButton";
import {
  trackFacilitySelection,
  trackRouteSearchStart,
  trackSearchQuery,
  trackCategorySelection,
} from "@/utils/analytics";
import { extractFloorInfo } from "@/features/pathFinding/utils/routingUtils";
import { useTranslation } from "react-i18next";
import {
  MdInfoOutline,
  MdKeyboardArrowDown,
  MdKeyboardArrowUp,
  MdOutlineArrowBackIos,
} from "react-icons/md";
import BaseButton from "./BaseButton";

type Category = "all" | "classroom" | "faculty" | "other";

type SharedRouteRequest = {
  goalFacilityId: string;
  mode: RoutingMode;
};

export interface ItemGroup {
  key: string;
  label: string;
  items: MetadataItem[];
}

interface SidebarProps {
  selectedBuilding: string | null;
  selectedFloor: number | null;
  // フロア変更完了後に resolve される Promise を返す
  onFloorChange: (buildingId: string, floor: number) => Promise<void>;
  onStartRouteSearch: (goalItem: MetadataItem) => void;
  onStartBarrierFreeRouteSearch: (goalItem: MetadataItem) => void;
  onRouteClear: () => void;
  onResetMap: () => void; // カメラを初期状態へ
  facilityIdToSelect?: string | null;
  currentRoutingMode: RoutingMode;
  hasAlternativeRoute?: boolean;
  sharedRouteRequest?: SharedRouteRequest | null;
  shareUrl?: string | null;
  isShareUrlLoading?: boolean;
  onFocusOnFacility: (facilityId: string, options?: { skipCameraSave?: boolean }) => void;
  onRestoreCameraPosition: () => void;
  onHighlightFacility: (facilityId: string, facilityName: string, floorId: string) => void;
  onClearHighlight: () => void;
  isMobile: boolean;
  isKioskMode?: boolean;
  currentHeight: number;
  onHeightChange: (height: number) => void;
  onOpenInfo: () => void;
  kioskResetKey?: number;
  onMobileDestButtonVisibleChange?: (visible: boolean) => void;
  mobileDestClickRef?: MutableRefObject<(() => void) | null>;
}

type ViewMode = "list" | "detail" | "route";

const Sidebar: React.FC<SidebarProps> = ({
  selectedBuilding,
  selectedFloor,
  onFloorChange,
  onStartRouteSearch,
  onStartBarrierFreeRouteSearch,
  onRouteClear,
  onResetMap,
  facilityIdToSelect,
  currentRoutingMode,
  hasAlternativeRoute = false,
  sharedRouteRequest = null,
  shareUrl = null,
  isShareUrlLoading = false,
  onFocusOnFacility,
  onRestoreCameraPosition,
  onHighlightFacility,
  onClearHighlight,
  isMobile,
  isKioskMode = false,
  currentHeight,
  onHeightChange,
  onOpenInfo,
  kioskResetKey,
  onMobileDestButtonVisibleChange,
  mobileDestClickRef,
}) => {
  const [allItems, setAllItems] = useState<MetadataItem[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<Category>("all");
  const [search, setSearch] = useState("");
  const [selectedItem, setSelectedItem] = useState<MetadataItem | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const [canUseBarrierFreeRoute, setCanUseBarrierFreeRoute] = useState<boolean>(false); // 詳細状態でのバリアフリー経路利用可否
  const [isMetadataLoading, setIsMetadataLoading] = useState<boolean>(true);
  const [isMobileOverlayOpen, setIsMobileOverlayOpen] = useState(false);
  const [isMobileSheetExpanded, setIsMobileSheetExpanded] = useState(true);

  const listContainerRef = useRef<HTMLDivElement | null>(null);
  const cardRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const isAutoScrollingRef = useRef(false);
  const isManualScrollingRef = useRef(false);
  const scrollEndTimerRef = useRef<NodeJS.Timeout | null>(null);
  const floorDetectionTimerRef = useRef<NodeJS.Timeout | null>(null);
  const scrollRafRef = useRef<number | null>(null);
  const initialScrollDoneRef = useRef(false);
  const savedScrollPositionRef = useRef<number>(0);
  const floorChangedInDetailOrRouteRef = useRef(false); // 詳細または経路表示中に階層が変更されたかどうか
  const isRouteSearchingRef = useRef(false); // 経路探索の実行中かどうか (連打による二重探索の防止)
  const pendingScrollKeyRef = useRef<string | null>(null); // 詳細または経路表示中に変更された階層のキー
  const sharedRouteHandledRef = useRef<string | null>(null);
  const searchDebounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastTrackedSearchRef = useRef<string>("");
  const previousFloorRef = useRef<{
    // 前回の階層情報を保持
    building: string | null;
    floor: number | null;
  }>({
    building: selectedBuilding,
    floor: selectedFloor,
  });
  const lastSelectedFacilityIdRef = useRef<string | null>(null);
  const isResettingRef = useRef(false);
  const scrollToCurrentFloorOnOpenRef = useRef(false); // SP: 目的地リストを開いた直後に現在階へスクロールするか
  const lastListHeightRef = useRef<number>(currentHeight);
  const sheetHandleRef = useRef<HTMLDivElement | null>(null);
  const mobileSheetHeaderRef = useRef<HTMLDivElement | null>(null);
  const mobileContentRef = useRef<HTMLDivElement | null>(null);
  const toggleSwipeStartYRef = useRef<number | null>(null);
  const closedToggleSwipeStartYRef = useRef<number | null>(null);
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? "ja";
  const getItemName = useCallback(
    (item: MetadataItem, lang: string) =>
      lang === "ja" ? item.name_japanese : (item.name_english ?? item.name_japanese),
    [],
  );
  const getFacultyTitle = (item: MetadataItem) => (item.faculty_title ? t(item.faculty_title) : "");
  const MOBILE_TOP_CLEARANCE_PX = 104;
  const MOBILE_COLLAPSED_HEIGHT_RATIO = 0.26;
  const MOBILE_COLLAPSED_MIN_HEIGHT_PX = 120;
  const getVisibleViewportHeight = useCallback(
    () => Math.round(window.visualViewport?.height ?? window.innerHeight),
    [],
  );

  const getCollapsedMobileSheetHeight = useCallback(
    () =>
      Math.max(
        MOBILE_COLLAPSED_MIN_HEIGHT_PX,
        Math.round(getVisibleViewportHeight() * MOBILE_COLLAPSED_HEIGHT_RATIO),
      ),
    [getVisibleViewportHeight],
  );

  const clampMobileSheetHeight = useCallback(
    (height: number) => {
      const minHeight = getCollapsedMobileSheetHeight();
      const maxHeight = Math.max(minHeight, getVisibleViewportHeight() - MOBILE_TOP_CLEARANCE_PX);
      return Math.max(minHeight, Math.min(height, maxHeight));
    },
    [getCollapsedMobileSheetHeight, getVisibleViewportHeight],
  );

  const autoFitMobileSheetToContent = useCallback(() => {
    if (!isMobile || viewMode === "list") {
      return;
    }

    const headerHeight = mobileSheetHeaderRef.current?.offsetHeight ?? 0;
    const handleHeight = sheetHandleRef.current?.offsetHeight ?? 0;
    const contentHeight = mobileContentRef.current?.scrollHeight ?? 0;
    if (contentHeight === 0) {
      return;
    }

    const nextHeight = clampMobileSheetHeight(
      Math.ceil(headerHeight + handleHeight + contentHeight),
    );
    if (Math.abs(currentHeight - nextHeight) > 4) {
      onHeightChange(nextHeight);
    }
  }, [clampMobileSheetHeight, currentHeight, isMobile, onHeightChange, viewMode]);

  useEffect(() => {
    fetchMetadata()
      .then((data) => setAllItems(Object.values(data)))
      .catch((err) => console.error("Failed to fetch metadata:", err))
      .finally(() => setIsMetadataLoading(false));
  }, []);

  // 詳細表示または経路表示中に階層が変更されたことを検知する
  useEffect(() => {
    if (viewMode === "detail" || viewMode === "route") {
      // 階層が変更された場合
      if (
        previousFloorRef.current.building !== selectedBuilding ||
        previousFloorRef.current.floor !== selectedFloor
      ) {
        floorChangedInDetailOrRouteRef.current = true;
        // 戻ったときに ItemCardList を当該階にスクロールするためのキーを保存
        if (selectedBuilding && selectedFloor !== null) {
          pendingScrollKeyRef.current = `${selectedBuilding}-${selectedFloor}`;
        }
      }
    }

    // 現在の階層を記録
    previousFloorRef.current = {
      building: selectedBuilding,
      floor: selectedFloor,
    };
  }, [selectedBuilding, selectedFloor, viewMode]);

  // 外部から施設IDで詳細画面を開く
  useEffect(() => {
    if (!facilityIdToSelect) {
      lastSelectedFacilityIdRef.current = null;
      return;
    }
    if (allItems.length === 0) return;
    if (facilityIdToSelect === lastSelectedFacilityIdRef.current) {
      return;
    }

    lastSelectedFacilityIdRef.current = facilityIdToSelect;

    const item = allItems.find((metadata) => metadata.id === facilityIdToSelect);
    if (item) {
      if (isMobile) {
        setIsMobileOverlayOpen(false);
        setIsMobileSheetExpanded(true);
      }
      setSelectedItem(item);
      setViewMode("detail");

      // 該当する施設のフロアに移動 -> 完了後にフォーカス & 吹き出し
      (async () => {
        await onFloorChange(item.building, item.floor);
        if (onFocusOnFacility) {
          onFocusOnFacility(item.id);
        }
        if (onHighlightFacility) {
          const floorId = `${item.building}-${item.floor}`;
          onHighlightFacility(item.id, getItemName(item, language), floorId);
        }
      })();
    }
  }, [
    facilityIdToSelect,
    allItems,
    getItemName,
    isMobile,
    onFloorChange,
    onFocusOnFacility,
    onHighlightFacility,
    language,
  ]);

  // 詳細画面を開いた際にバリアフリー経路が利用可能かチェック
  useEffect(() => {
    // 詳細モードでない、または選択されたアイテムがない場合は処理しない
    if (viewMode !== "detail" || !selectedItem) {
      setCanUseBarrierFreeRoute(false);
      return;
    }

    // バリアフリー経路のチェック
    const checkBarrierFreeRoute = async () => {
      setCanUseBarrierFreeRoute(false);
      const startLocationInfo = DIGITAL_SIGNAGE_LOCATION;

      if (!startLocationInfo) {
        return;
      }

      // スタート地点の設定
      const start: LocationInput = {
        position: {
          x: startLocationInfo.position.x,
          y: startLocationInfo.position.y,
        },
        floorId: startLocationInfo.floorId,
      };
      // ゴール地点の設定
      const goal: LocationInput = selectedItem.id;

      try {
        // 別の階層にある施設間ではバリアフリールートを表示する

        const goal_floorInfo = extractFloorInfo(goal);
        const isDifferentFloor = start.floorId !== goal_floorInfo?.floorId;

        setCanUseBarrierFreeRoute(isDifferentFloor);
      } catch (error) {
        console.error("バリアフリー経路のチェックに失敗:", error);
      }
    };

    checkBarrierFreeRoute();
  }, [viewMode, selectedItem]);

  const filteredForGroups = useMemo(
    () => filterItems(allItems, { search, category: selectedCategory }),
    [allItems, search, selectedCategory],
  );

  const groupedItems = useMemo(
    (): ItemGroup[] => groupItemsByFloor(filteredForGroups),
    [filteredForGroups],
  );

  // 各カテゴリの件数を、現在の検索条件で計算してボタンの無効化に使う
  const countsByCategory = useMemo(() => getCountsByCategory(allItems, search), [allItems, search]);

  useEffect(() => {
    if (searchDebounceTimerRef.current) {
      clearTimeout(searchDebounceTimerRef.current);
    }

    if (!search) {
      lastTrackedSearchRef.current = "";
      return;
    }

    searchDebounceTimerRef.current = setTimeout(() => {
      if (lastTrackedSearchRef.current === search) {
        return;
      }
      trackSearchQuery(search);
      lastTrackedSearchRef.current = search;
    }, 1500);

    return () => {
      if (searchDebounceTimerRef.current) {
        clearTimeout(searchDebounceTimerRef.current);
      }
    };
  }, [search]);

  /**
   * ItemCardList のスクロールイベントを処理する
   */
  const handleListScroll = useCallback(() => {
    if (isAutoScrollingRef.current) return;

    // スクロール中フラグは即座に立てる
    isManualScrollingRef.current = true;
    if (scrollEndTimerRef.current) clearTimeout(scrollEndTimerRef.current);
    scrollEndTimerRef.current = setTimeout(() => {
      isManualScrollingRef.current = false;
    }, 100);

    // getBoundingClientRect ループは 50ms debounce してレイアウトスラッシングを防ぐ
    if (floorDetectionTimerRef.current) clearTimeout(floorDetectionTimerRef.current);
    floorDetectionTimerRef.current = setTimeout(() => {
      const container = listContainerRef.current;
      const refs = cardRefs.current;
      if (!container || !refs) return;

      const containerStyle = window.getComputedStyle(container);
      const containerPaddingTop = parseFloat(containerStyle.paddingTop) || 0;
      const containerRect = container.getBoundingClientRect();
      const scrollAreaTop = containerRect.top + containerPaddingTop;

      let closestKey: string | null = null;
      let smallestDistance = Infinity;

      for (const key in refs) {
        const element = refs[key];
        if (!element) continue;

        const itemTop = element.getBoundingClientRect().top;

        if (itemTop <= scrollAreaTop) {
          const distance = itemTop - scrollAreaTop;
          if (distance > smallestDistance || smallestDistance === Infinity) {
            smallestDistance = distance;
            closestKey = key;
          }
        }
      }

      if (closestKey) {
        const [building, floorStr] = closestKey.split("-");
        const floor = parseInt(floorStr, 10);
        if (building !== selectedBuilding || floor !== selectedFloor) {
          onFloorChange(building, floor);
        }
      }
    }, 50);
  }, [listContainerRef, cardRefs, onFloorChange, selectedBuilding, selectedFloor]);

  useEffect(() => {
    if (isManualScrollingRef.current) {
      return;
    }
    if (!selectedBuilding || selectedFloor === null) {
      return;
    }

    const key = `${selectedBuilding}-${selectedFloor}`;
    const targetCard = cardRefs.current?.[key];
    const listContainer = listContainerRef.current;

    if (targetCard && listContainer) {
      isAutoScrollingRef.current = true;

      if (scrollRafRef.current !== null) cancelAnimationFrame(scrollRafRef.current);
      scrollRafRef.current = requestAnimationFrame(() => {
        scrollRafRef.current = null;
        const containerStyle = window.getComputedStyle(listContainer);
        const containerPaddingTop = parseFloat(containerStyle.paddingTop) || 0;
        const containerRect = listContainer.getBoundingClientRect();
        const scrollAreaTop = containerRect.top + containerPaddingTop;
        const targetRect = targetCard.getBoundingClientRect();
        const scrollAmount = targetRect.top - scrollAreaTop;

        listContainer.scrollTo({
          top: listContainer.scrollTop + scrollAmount,
          behavior: "auto",
        });
        if (scrollEndTimerRef.current) clearTimeout(scrollEndTimerRef.current);
        scrollEndTimerRef.current = setTimeout(() => {
          isAutoScrollingRef.current = false;
          isManualScrollingRef.current = false;
        }, 100);
      });
    }

    return () => {
      if (scrollRafRef.current !== null) {
        cancelAnimationFrame(scrollRafRef.current);
        scrollRafRef.current = null;
      }
    };
  }, [selectedBuilding, selectedFloor]);

  // 初回ロード時に ItemCardList を main の 3階が見える位置にスクロールする
  useEffect(() => {
    if (initialScrollDoneRef.current) return;

    const targetKey = `main-3`;
    const targetCard = cardRefs.current?.[targetKey];
    const listContainer = listContainerRef.current;

    if (targetCard && listContainer) {
      isAutoScrollingRef.current = true;

      if (scrollRafRef.current !== null) cancelAnimationFrame(scrollRafRef.current);
      scrollRafRef.current = requestAnimationFrame(() => {
        scrollRafRef.current = null;
        const containerStyle = window.getComputedStyle(listContainer);
        const containerPaddingTop = parseFloat(containerStyle.paddingTop) || 0;
        const containerRect = listContainer.getBoundingClientRect();
        const scrollAreaTop = containerRect.top + containerPaddingTop;
        const targetRect = targetCard.getBoundingClientRect();
        const scrollAmount = targetRect.top - scrollAreaTop;

        listContainer.scrollTo({
          top: listContainer.scrollTop + scrollAmount,
          behavior: "auto",
        });

        if (scrollEndTimerRef.current) clearTimeout(scrollEndTimerRef.current);
        scrollEndTimerRef.current = setTimeout(() => {
          isAutoScrollingRef.current = false;
          initialScrollDoneRef.current = true;
        }, 100);
      });
    }

    return () => {
      if (scrollRafRef.current !== null) {
        cancelAnimationFrame(scrollRafRef.current);
        scrollRafRef.current = null;
      }
    };
  }, [groupedItems]);

  /**
   * ItemCard がクリックされたときに呼び出されるハンドラ
   */
  const handleItemSelect = useCallback(
    (item: MetadataItem) => {
      // 現在のスクロール位置を保存
      if (listContainerRef.current) {
        savedScrollPositionRef.current = listContainerRef.current.scrollTop;
      }

      // 詳細表示に切り替える際にフラグをリセット
      floorChangedInDetailOrRouteRef.current = false;

      if (isMobile) {
        setIsMobileOverlayOpen(false);
        setIsMobileSheetExpanded(true);
      }
      setSelectedItem(item);
      setViewMode("detail");

      trackFacilitySelection({
        facilityId: item.id,
        facilityName: item.name_japanese,
        selectionSource: "sidebar",
      });

      // 該当する施設のフロアに移動後にフォーカス/吹き出し
      (async () => {
        await onFloorChange(item.building, item.floor);
        if (onFocusOnFacility) {
          onFocusOnFacility(item.id);
        }
        if (onHighlightFacility) {
          const floorId = `${item.building}-${item.floor}`;
          onHighlightFacility(item.id, getItemName(item, language), floorId);
        }
      })();
    },
    [getItemName, isMobile, language, onFloorChange, onFocusOnFacility, onHighlightFacility],
  );

  /**
   * 詳細画面の「戻る」ボタンが押されたときに呼び出されるハンドラ
   */
  const handleBackFromDetail = () => {
    setSelectedItem(null);
    setViewMode("list");
    setCanUseBarrierFreeRoute(false);

    if (isMobile) {
      setIsMobileSheetExpanded(true);
      setIsMobileOverlayOpen(true);
    }

    // ハイライトをクリアしてカメラ位置を復元
    onClearHighlight();
    onRestoreCameraPosition();

    // 詳細表示中に階層が変更されていた場合は、その階層位置にスクロール
    if (floorChangedInDetailOrRouteRef.current) {
      const key = pendingScrollKeyRef.current; // 保存していたキーを取得
      pendingScrollKeyRef.current = null; // クリア
      floorChangedInDetailOrRouteRef.current = false;

      if (key) {
        requestAnimationFrame(() => {
          const targetCard = cardRefs.current?.[key];
          const listContainer = listContainerRef.current;

          // スクロール処理
          if (targetCard && listContainer) {
            isAutoScrollingRef.current = true;

            const containerStyle = window.getComputedStyle(listContainer);
            const containerPaddingTop = parseFloat(containerStyle.paddingTop) || 0;
            const containerRect = listContainer.getBoundingClientRect();
            const scrollAreaTop = containerRect.top + containerPaddingTop;
            const targetRect = targetCard.getBoundingClientRect();
            const scrollAmount = targetRect.top - scrollAreaTop;

            listContainer.scrollTo({
              top: listContainer.scrollTop + scrollAmount,
              behavior: "auto",
            });

            if (scrollEndTimerRef.current) {
              clearTimeout(scrollEndTimerRef.current);
            }
            scrollEndTimerRef.current = setTimeout(() => {
              isAutoScrollingRef.current = false;
            }, 100);
          }
        });
      }
    } else {
      // 階層変更がなければ、保存していたスクロール位置を復元
      requestAnimationFrame(() => {
        if (listContainerRef.current && savedScrollPositionRef.current > 0) {
          isAutoScrollingRef.current = true;
          listContainerRef.current.scrollTo({
            top: savedScrollPositionRef.current,
            behavior: "auto",
          });

          if (scrollEndTimerRef.current) {
            clearTimeout(scrollEndTimerRef.current);
          }
          scrollEndTimerRef.current = setTimeout(() => {
            isAutoScrollingRef.current = false;
          }, 50);
        }
      });
    }
  };

  // ホームに戻る（詳細/経路表示を終了し初期リスト状態へ）
  const handleGoHome = () => {
    onRouteClear(); // 地図上のルート表示をクリア
    setSelectedItem(null); // 選択アイテムをクリア
    setCanUseBarrierFreeRoute(false); // バリアフリー経路利用可否をリセット
    onClearHighlight(); // ハイライトをクリア
    onResetMap(); // カメラを初期状態へ

    // 検索とカテゴリをクリア
    setSearch("");
    setSelectedCategory("all");

    // 過去のスクロール記憶を消去
    savedScrollPositionRef.current = 0; // スクロール位置リセット
    pendingScrollKeyRef.current = null; // 階数キーリセット
    initialScrollDoneRef.current = false; // 初回スクロール済みフラグリセット

    // リセット中フラグを立てる
    isResettingRef.current = true;

    // リストモードへ変更
    if (isMobile) {
      setIsMobileOverlayOpen(false);
      setIsMobileSheetExpanded(true);
    }
    setViewMode("list");
    // ここで関数終了。あとは useEffect に任せる
  };

  // キオスク非アクティブタイムアウト時にサイドバーを初期状態へリセット
  useEffect(() => {
    if (!kioskResetKey) return;
    if (isMobile) {
      setIsMobileOverlayOpen(false);
      setIsMobileSheetExpanded(true);
    }
    handleGoHome();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, kioskResetKey]);

  // リスト表示に戻ったタイミングで、リセットフラグが立っていればスクロールを実行
  useEffect(() => {
    // 条件: リストモードであり、かつリセット操作直後であること
    if (viewMode === "list" && isResettingRef.current) {
      const targetKey = "main-3";
      const targetCard = cardRefs.current?.[targetKey]; // 「Main 3階」のカード（DOM要素）
      const listContainer = listContainerRef.current; // スクロールする外側の箱（DOM要素）

      if (targetCard && listContainer) {
        const containerStyle = window.getComputedStyle(listContainer); // スクロールコンテナのスタイル情報を取得
        const containerPaddingTop = parseFloat(containerStyle.paddingTop) || 0; // 上部パディングの数値を取得
        const containerRect = listContainer.getBoundingClientRect(); // コンテナの位置情報を取得

        // 「リスト表示領域」の本当のスタート位置（上端 + パディング）
        const scrollAreaTop = containerRect.top + containerPaddingTop;

        // 「Main 3階カード」の現在位置
        const targetRect = targetCard.getBoundingClientRect();

        // スクロールすべき量を計算
        const scrollAmount = targetRect.top - scrollAreaTop;

        listContainer.scrollTo({
          // 現在のスクロール位置に、計算したズレ（scrollAmount）を足す
          top: listContainer.scrollTop + scrollAmount,
          behavior: "auto",
        });
      } else if (listContainer) {
        listContainer.scrollTop = 0;
      }

      // 処理が終わったらフラグを下ろす（重要）
      isResettingRef.current = false;
    }
  }, [viewMode, groupedItems]); // viewModeが変わった時、またはリストの中身が変わった時にチェック

  // SP: 目的地リストは閉じるとアンマウントされ、開くたびに先頭(本部棟1階)から描画される。
  // 開いた直後に地図の現在階のグループまでスクロールし、階数セレクタと表示位置を揃える
  useEffect(() => {
    if (!scrollToCurrentFloorOnOpenRef.current) return;
    if (!isMobileOverlayOpen || viewMode !== "list" || isMetadataLoading) return;
    scrollToCurrentFloorOnOpenRef.current = false;
    if (!selectedBuilding || selectedFloor === null) return;

    const targetCard = cardRefs.current?.[`${selectedBuilding}-${selectedFloor}`];
    const listContainer = listContainerRef.current;
    if (!targetCard || !listContainer) return;

    isAutoScrollingRef.current = true;
    initialScrollDoneRef.current = true; // 以降の検索入力で初回スクロール(main-3)が走らないようにする

    const containerStyle = window.getComputedStyle(listContainer);
    const containerPaddingTop = parseFloat(containerStyle.paddingTop) || 0;
    const containerRect = listContainer.getBoundingClientRect();
    const scrollAreaTop = containerRect.top + containerPaddingTop;
    const targetRect = targetCard.getBoundingClientRect();
    const scrollAmount = targetRect.top - scrollAreaTop;

    listContainer.scrollTo({
      top: listContainer.scrollTop + scrollAmount,
      behavior: "auto",
    });

    if (scrollEndTimerRef.current) clearTimeout(scrollEndTimerRef.current);
    scrollEndTimerRef.current = setTimeout(() => {
      isAutoScrollingRef.current = false;
    }, 100);
  }, [isMobileOverlayOpen, viewMode, isMetadataLoading, selectedBuilding, selectedFloor]);

  /**
   * 経路表示画面の「戻る」ボタンが押されたときに呼び出されるハンドラ
   */
  const handleBackFromRoute = () => {
    onRouteClear(); // 地図上のルート表示をクリア
    setViewMode("detail");

    // 選択されているアイテムの階層に移動してフォーカス
    if (selectedItem) {
      (async () => {
        await onFloorChange(selectedItem.building, selectedItem.floor);
        if (onRestoreCameraPosition) {
          onRestoreCameraPosition();
        }
        if (onFocusOnFacility) {
          onFocusOnFacility(selectedItem.id, { skipCameraSave: true });
        }
        if (onHighlightFacility) {
          const floorId = `${selectedItem.building}-${selectedItem.floor}`;
          onHighlightFacility(selectedItem.id, getItemName(selectedItem, language), floorId);
        }
      })();
    }
  };

  /**
   * デフォルト経路探索を開始
   */
  const trackRouteStartForItem = (item: MetadataItem, routeType: "normal" | "barrier_free") => {
    trackRouteSearchStart({
      facilityId: item.id,
      facilityName: item.name_japanese,
      routeType,
    });
  };

  const startRouteSearch = async (
    item: MetadataItem,
    routeType: "normal" | "barrier_free",
    search: (goalItem: MetadataItem) => void | Promise<void>,
  ) => {
    // 探索中にもう一度押されても経路探索を二重に走らせない
    if (isRouteSearchingRef.current) return;
    isRouteSearchingRef.current = true;
    try {
      floorChangedInDetailOrRouteRef.current = false;
      if (isMobile) {
        setIsMobileSheetExpanded(true);
      }
      trackRouteStartForItem(item, routeType);
      await search(item);
      setViewMode("route");
      void onFloorChange("main", 3); // 非同期で開始
    } finally {
      isRouteSearchingRef.current = false;
    }
  };

  const handleStartDefaultRoute = (item: MetadataItem) =>
    startRouteSearch(item, "normal", onStartRouteSearch);

  /**
   * バリアフリー経路探索を開始
   */
  const handleStartBarrierFreeRoute = (item: MetadataItem) =>
    startRouteSearch(item, "barrier_free", onStartBarrierFreeRouteSearch);

  useEffect(() => {
    if (!sharedRouteRequest || allItems.length === 0) {
      return;
    }
    const { goalFacilityId, mode } = sharedRouteRequest;
    const requestKey = `${goalFacilityId}-${mode}`;
    if (sharedRouteHandledRef.current === requestKey) {
      return;
    }
    const targetItem = allItems.find((item) => item.id === goalFacilityId);
    if (!targetItem) {
      return;
    }

    sharedRouteHandledRef.current = requestKey;
    if (isMobile) {
      setIsMobileOverlayOpen(false);
      setIsMobileSheetExpanded(true);
    }
    setSelectedItem(targetItem);
    floorChangedInDetailOrRouteRef.current = false;
    setViewMode("route");
    onFloorChange("main", 3);

    if (mode === "barrier-free") {
      onStartBarrierFreeRouteSearch(targetItem);
    } else {
      onStartRouteSearch(targetItem);
    }
  }, [
    sharedRouteRequest,
    allItems,
    onStartRouteSearch,
    onStartBarrierFreeRouteSearch,
    onFloorChange,
    isMobile,
  ]);

  const handleCategorySelect = (category: Category) => {
    setSelectedCategory(category);
    trackCategorySelection(category);
  };

  useEffect(() => {
    if (!isMobile) {
      return;
    }
    if (viewMode === "list") {
      lastListHeightRef.current = currentHeight;
    }
  }, [currentHeight, isMobile, viewMode]);

  useEffect(() => {
    if (!isMobile || viewMode === "list") {
      return;
    }

    const frameId = window.requestAnimationFrame(() => {
      autoFitMobileSheetToContent();
    });

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [autoFitMobileSheetToContent, currentRoutingMode, isMobile, selectedItem?.id, viewMode]);

  useEffect(() => {
    if (!isMobile || viewMode === "list" || typeof ResizeObserver === "undefined") {
      return;
    }

    const contentElement = mobileContentRef.current;
    if (!contentElement) {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      autoFitMobileSheetToContent();
    });
    resizeObserver.observe(contentElement);

    return () => {
      resizeObserver.disconnect();
    };
  }, [autoFitMobileSheetToContent, isMobile, viewMode]);

  useEffect(() => {
    if (!isMobile) {
      setIsMobileOverlayOpen(false);
      setIsMobileSheetExpanded(true);
    }
  }, [isMobile]);

  const handleOpenMobileOverlay = useCallback(() => {
    scrollToCurrentFloorOnOpenRef.current = true;
    setViewMode("list");
    setIsMobileOverlayOpen(true);
  }, []);

  const handleCloseMobileOverlay = useCallback(() => {
    setIsMobileOverlayOpen(false);
  }, []);

  const showMobileDestButton = isMobile;

  useEffect(() => {
    if (mobileDestClickRef) mobileDestClickRef.current = handleOpenMobileOverlay;
  }, [mobileDestClickRef, handleOpenMobileOverlay]);

  useEffect(() => {
    onMobileDestButtonVisibleChange?.(showMobileDestButton);
  }, [showMobileDestButton, onMobileDestButtonVisibleChange]);

  const handleExpandMobileSheet = () => {
    setIsMobileSheetExpanded(true);
  };

  const handleCollapseMobileSheet = () => {
    setIsMobileSheetExpanded(false);
  };

  const handleSheetTogglePointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    toggleSwipeStartYRef.current = e.clientY;
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const handleSheetTogglePointerUp = (e: React.PointerEvent<HTMLButtonElement>) => {
    const startY = toggleSwipeStartYRef.current;
    toggleSwipeStartYRef.current = null;
    if (startY === null) return;
    if (e.clientY - startY > 24) {
      handleCollapseMobileSheet();
    }
  };

  const handleSheetTogglePointerCancel = () => {
    toggleSwipeStartYRef.current = null;
  };

  const handleClosedTogglePointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    closedToggleSwipeStartYRef.current = e.clientY;
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const handleClosedTogglePointerUp = (e: React.PointerEvent<HTMLButtonElement>) => {
    const startY = closedToggleSwipeStartYRef.current;
    closedToggleSwipeStartYRef.current = null;
    if (startY === null) return;
    if (startY - e.clientY > 24) {
      handleExpandMobileSheet();
    }
  };

  const handleClosedTogglePointerCancel = () => {
    closedToggleSwipeStartYRef.current = null;
  };

  const handleMobileOverlayBack = () => {
    if (viewMode === "detail") {
      handleBackFromDetail();
      return;
    }
    if (viewMode === "route") {
      handleBackFromRoute();
      return;
    }
    handleCloseMobileOverlay();
  };

  const isMobileFullscreenList = isMobile && viewMode === "list";
  const isMobileBottomSheet = isMobile && viewMode !== "list";
  const shouldRenderMobileSheet = isMobileBottomSheet && isMobileSheetExpanded;

  const shouldRenderMobileOverlay = !isMobile || isMobileOverlayOpen || shouldRenderMobileSheet;

  return (
    <>
      {/* 目的地ボタンはApp.tsx側のunified mobile top barで描画 */}

      {/* SPの重なり順: 現在地ボタン(z-50) < ボトムシート(z-54) < トップバーと階数ドロップダウン(z-55)
          < 全画面の目的地リスト(z-60)。シートは高さ上限でトップバーと重ならないが、
          トップバーから下に開くドロップダウンはシートより前面に出す必要がある */}
      {isMobileBottomSheet && !isMobileSheetExpanded && (
        <div className="fixed bottom-28 left-1/2 z-[54] -translate-x-1/2">
          <button
            type="button"
            onClick={handleExpandMobileSheet}
            onPointerDown={handleClosedTogglePointerDown}
            onPointerUp={handleClosedTogglePointerUp}
            onPointerCancel={handleClosedTogglePointerCancel}
            className="inline-flex min-h-[44px] items-center justify-center gap-1 rounded-full border border-Border bg-white/78 px-8 py-2 text-TextPrimary shadow-[0_4px_14px_rgba(0,0,0,0.10)] backdrop-blur-md transition-colors hover:bg-white/88"
          >
            <MdKeyboardArrowUp className="text-[20px]" />
            <Text as="span" variant="body/s" weight="bold">
              {t("詳細を開く")}
            </Text>
          </button>
        </div>
      )}

      {shouldRenderMobileOverlay && (
        <aside
          className={`
            ${
              isMobileFullscreenList
                ? "fixed inset-0 z-[60] h-dvh w-full"
                : shouldRenderMobileSheet
                  ? "fixed inset-x-0 bottom-0 z-[54] w-full rounded-t-[20px] shadow-[0_-4px_30px_rgba(0,0,0,0.10)]"
                  : "relative w-[32rem] h-full border-r-2 border-Border"
            }
            bg-white flex flex-col items-start overflow-x-hidden
          `}
          style={{
            height: shouldRenderMobileSheet ? `${currentHeight}px` : undefined,
          }}
        >
          {shouldRenderMobileSheet && (
            <div
              ref={sheetHandleRef}
              className="relative flex w-full shrink-0 items-start px-4 pb-3 pt-3"
            >
              <button
                type="button"
                onClick={handleMobileOverlayBack}
                className="inline-flex min-h-[44px] shrink-0 items-center gap-1 rounded-2xl border border-Border bg-Surface px-4 py-2 text-TextPrimary transition-colors duration-150 ease-in-out hover:bg-Background"
              >
                <MdOutlineArrowBackIos className="shrink-0 text-[16px]" />
                <Text as="span" variant="body/s" weight="bold">
                  {t("戻る")}
                </Text>
              </button>
              <button
                type="button"
                onClick={handleCollapseMobileSheet}
                onPointerDown={handleSheetTogglePointerDown}
                onPointerUp={handleSheetTogglePointerUp}
                onPointerCancel={handleSheetTogglePointerCancel}
                className="absolute left-1/2 top-0 inline-flex h-9 w-14 -translate-x-1/2 items-center justify-center rounded-t-none rounded-b-full border-b border-l border-r border-Border bg-white text-TextPrimary shadow-[0_4px_10px_rgba(0,0,0,0.08)] transition-colors hover:bg-Background"
              >
                <MdKeyboardArrowDown className="text-[24px]" />
              </button>
            </div>
          )}

          {isMobileFullscreenList && (
            <div
              ref={mobileSheetHeaderRef}
              className="flex w-full shrink-0 items-center justify-between gap-4 px-4 pb-3 pt-4"
            >
              <button
                type="button"
                onClick={handleMobileOverlayBack}
                className="inline-flex min-h-[44px] shrink-0 items-center gap-1 rounded-2xl border border-Border bg-Surface px-4 py-2 text-TextPrimary transition-colors duration-150 ease-in-out hover:bg-Background"
              >
                <MdOutlineArrowBackIos className="shrink-0 text-[16px]" />
                <Text as="span" variant="body/s" weight="bold">
                  {t("戻る")}
                </Text>
              </button>
              <BaseButton
                type="button"
                onClick={onOpenInfo}
                icon={MdInfoOutline}
                textVariant="body/s"
                className="shrink-0"
              >
                {t("このアプリについて")}
              </BaseButton>
            </div>
          )}

          {viewMode === "list" && (
            <>
              {/* 検索エリア */}
              <div className="w-full pt-3 px-4 sm:pt-8 sm:px-8 flex flex-col items-start shrink-0">
                <Text as="h2" variant="heading/m" weight="bold" className="hidden sm:block mb-2">
                  {t("施設名や教員名で探す")}
                </Text>
                <div className="w-full">
                  <SearchWithVirtualkeyboard
                    value={search}
                    onChange={setSearch}
                    resetKey={kioskResetKey}
                    resultCount={filteredForGroups.length}
                  />
                </div>
              </div>

              {/* カテゴリー */}
              <div className="w-full px-4 pt-4 sm:px-8 sm:pt-8 flex flex-col items-start shrink-0">
                <Text as="h2" variant="heading/m" weight="bold" className="hidden sm:block mb-2">
                  {t("カテゴリー")}
                </Text>
                <CategorySelector
                  selectedCategory={selectedCategory}
                  onSelectCategory={handleCategorySelect}
                  counts={countsByCategory}
                />
              </div>

              {/* 階数セレクタ＋カードリスト */}
              {isMobile ? (
                <div className="w-full flex flex-col flex-1 min-h-0 px-4 pt-4 pb-6 gap-3">
                  <div className="shrink-0">
                    <SidebarFloorSelector
                      BUILDINGS={BUILDINGS}
                      filteredItems={groupedItems.flatMap((g) => g.items)}
                      selectedBuilding={selectedBuilding}
                      selectedFloor={selectedFloor}
                      onFloorSelect={onFloorChange}
                      cardRefs={cardRefs}
                      listContainerRef={listContainerRef}
                      isMobile={true}
                    />
                  </div>
                  <ItemCardList
                    groups={groupedItems}
                    listContainerRef={listContainerRef}
                    cardRefs={cardRefs}
                    onScroll={handleListScroll}
                    className="flex-1 min-h-0"
                    onItemSelect={handleItemSelect}
                    isLoading={isMetadataLoading}
                    isMobile={true}
                  />
                </div>
              ) : (
                <div className="w-full flex flex-row items-stretch -mx-2 flex-1 min-h-0 gap-3 px-4 pt-4 pb-6 sm:gap-6 sm:px-8 sm:pt-8">
                  <div className="flex flex-col gap-4 items-center justify-between">
                    <SidebarFloorSelector
                      BUILDINGS={BUILDINGS}
                      filteredItems={groupedItems.flatMap((g) => g.items)}
                      selectedBuilding={selectedBuilding}
                      selectedFloor={selectedFloor}
                      onFloorSelect={onFloorChange}
                      cardRefs={cardRefs}
                      listContainerRef={listContainerRef}
                    />
                    <InformationButton onClick={onOpenInfo} />
                  </div>
                  <div className="w-full flex-1 min-h-0 flex flex-col">
                    <ItemCardList
                      groups={groupedItems}
                      listContainerRef={listContainerRef}
                      cardRefs={cardRefs}
                      onScroll={handleListScroll}
                      className="flex-1 min-h-0"
                      onItemSelect={handleItemSelect}
                      isLoading={isMetadataLoading}
                    />
                  </div>
                </div>
              )}
            </>
          )}

          {viewMode === "detail" && selectedItem && (
            <SidebarFacilityInformation
              containerRef={mobileContentRef}
              isMobile={isMobile}
              className={isMobile ? "pt-0" : ""}
              itemId={selectedItem.id}
              floorName={t(
                `${
                  BUILDINGS.find((b) => b.id === selectedItem.building)?.label || ""
                } ${selectedItem.floor}階`,
              )}
              roomLabel={getItemName(selectedItem, language)}
              roomSubLabel={
                language === "ja"
                  ? selectedItem.faculty_name_ja
                    ? `${selectedItem.faculty_name_ja} ${getFacultyTitle(selectedItem)}`
                    : undefined
                  : language === "en"
                    ? selectedItem.faculty_name_en
                      ? `${getFacultyTitle(selectedItem)} ${selectedItem.faculty_name_en}`
                      : undefined
                    : selectedItem.faculty_name_en
                      ? `${getFacultyTitle(selectedItem)} ${selectedItem.faculty_name_en}`
                      : undefined
              }
              description={
                language === "ja"
                  ? selectedItem.description_ja
                  : language === "en"
                    ? selectedItem.description_en
                    : selectedItem.description_en
              }
              hasOstomate={selectedItem.has_ostomate === true}
              onBack={isMobile ? undefined : handleBackFromDetail}
              onGoHome={isMobile ? undefined : handleGoHome}
              onViewDefaultRoute={() => handleStartDefaultRoute(selectedItem)}
              onViewBarrierfreeRoute={() => handleStartBarrierFreeRoute(selectedItem)}
              isElevatorButtonVisible={canUseBarrierFreeRoute}
            />
          )}

          {viewMode === "route" && selectedItem && (
            <SidebarNormalRoute
              containerRef={mobileContentRef}
              className={isMobile ? "pt-0" : ""}
              floorName={`${
                BUILDINGS.find((b) => b.id === selectedItem.building)?.label || ""
              } ${selectedItem.floor}階`}
              roomLabel={getItemName(selectedItem, language)}
              faculty_name={
                language === "ja"
                  ? selectedItem.faculty_name_ja
                    ? `${selectedItem.faculty_name_ja}${selectedItem.faculty_title ? ` ${getFacultyTitle(selectedItem)}` : ""}`
                    : undefined
                  : language === "en"
                    ? selectedItem.faculty_name_en
                      ? `${selectedItem.faculty_name_en}${selectedItem.faculty_title ? ` ${getFacultyTitle(selectedItem)}` : ""}`
                      : undefined
                    : selectedItem.faculty_name_en
                      ? `${selectedItem.faculty_name_en}${selectedItem.faculty_title ? ` ${getFacultyTitle(selectedItem)}` : ""}`
                      : undefined
              }
              onBack={isMobile ? undefined : handleBackFromRoute}
              onGoHome={isMobile ? undefined : handleGoHome}
              onViewDefaultRoute={
                currentRoutingMode === "barrier-free" && hasAlternativeRoute
                  ? () => handleStartDefaultRoute(selectedItem)
                  : undefined
              }
              onViewBarrierfreeRoute={
                currentRoutingMode === "default" && hasAlternativeRoute
                  ? () => handleStartBarrierFreeRoute(selectedItem)
                  : undefined
              }
              currentRoutingMode={currentRoutingMode}
              isElevatorButtonVisible={hasAlternativeRoute}
              shareUrl={shareUrl}
              isShareUrlLoading={isShareUrlLoading}
              isMobile={isMobile}
              isKioskMode={isKioskMode}
              // 親(MapClient)から受け取った「高さを変える関数」をそのまま渡す
              onHeightChange={onHeightChange}
            />
          )}
        </aside>
      )}
    </>
  );
};

export default Sidebar;
